import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as finService from "../../services/finService";
import useModuleAccess from "../../hooks/useModuleAccess";
import { hasModulePermission } from "../../constants/platformModules";
import useFinOptions from "./useFinOptions";
import useGlContext, { fmtMoney } from "./gl/glContext";
import AccountPicker from "./gl/AccountPicker";
import ReceiptForm from "./cash/ReceiptForm";
import { Badge, DataTable, act, hint, linkBtn } from "./gl/glUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, formatDate, formatDateTime, inputStyle, pageStyle, todayYmd, useLoad } from "../../components/ui";

// Cash & Bank: receipts, cheques (PDC), cash drawers, petty cash, write-offs and the aging.
function useRoles() {
  const { access } = useModuleAccess();
  return {
    treasury: hasModulePermission(access, "FIN", "FIN_AR", "FIN_CONTROLLER", "FIN_ACCOUNTANT"),
    cashier: hasModulePermission(access, "FIN", "FIN_AR", "FIN_CONTROLLER", "FIN_ACCOUNTANT", "DMS_CASHIER", "DMS_SALES_MANAGER", "DMS_ADMIN"),
  };
}

/* ------------------------------ receipts ------------------------------ */

function ReceiptView({ id, onHide, onChanged }) {
  const roles = useRoles();
  const { data: r, loading, error, reload } = useLoad(() => finService.getReceipt(id), [id]);
  const open = useLoad(() => (r ? finService.listTransactions({ customer_account_id: r.customer_account_id, open: 1, size: 200 }) : Promise.resolve({ success: true, data: [] })), [r && r.receipt_number]);
  const [form, setForm] = useState(null);
  const refresh = () => {
    reload();
    onChanged();
  };
  return (
    <SlidePanel show onHide={onHide} title={r ? `Receipt ${r.receipt_number}` : "Receipt"} width={820}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {r ? (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <PrimaryButton onClick={() => finService.receiptPdf(id, `${r.receipt_number}.pdf`)}>Receipt PDF</PrimaryButton>
              {r.status !== "REVERSED" && Number(r.unapplied_amount) > 0 ? <SecondaryButton onClick={() => setForm("apply")}>Apply to an invoice</SecondaryButton> : null}
              {roles.treasury && r.status !== "REVERSED" && !r.pdc_id ? <SecondaryButton onClick={() => setForm("reverse")}>Reverse</SecondaryButton> : null}
            </div>
            <div style={{ fontSize: 14, marginBottom: 12 }}>
              <b>{r.customer_name}</b> ({r.account_number}) · {formatDate(r.receipt_date)} · {r.method_name} {r.reference ? `· ${r.reference}` : ""} <Badge status={r.status === "CONFIRMED" ? "POSTED" : r.status === "REVERSED" ? "REVERSED" : r.status} />
              <div>
                {r.currency} {fmtMoney(r.amount)} · applied {fmtMoney(r.applied_amount)} · {r.purpose === "ADVANCE" ? "advance" : "unapplied"} {fmtMoney(r.unapplied_amount)}
                {r.source_doc_number ? ` · for ${r.source_doc_number}` : ""}
              </div>
              {r.cheque ? <div style={hint}>Cheque {r.cheque.cheque_number} ({r.cheque.drawee_bank}) dated {formatDate(r.cheque.cheque_date)} · {r.cheque.pdc_number} {r.cheque.status.toLowerCase()}</div> : null}
              {r.reversal_reason ? <div style={{ color: "#991B1B" }}>{r.reversal_reason}</div> : null}
            </div>
            <DataTable
              rows={r.applications}
              rowKey={(a, i) => i}
              minWidth={480}
              empty={<div style={hint}>Not applied to any invoice.</div>}
              columns={[
                { label: "Invoice", render: (a) => a.trx_number },
                { label: "Date", render: (a) => formatDate(a.apply_date) },
                { label: "Amount", num: true, render: (a) => fmtMoney(a.amount) },
                { label: "", render: (a) => (a.reversed ? "Reversed" : "") },
              ]}
            />
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={form === "apply"}
        title="Apply to an invoice"
        initial={{}}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => finService.applyReceipt(id, clean.trx_id, clean.amount ? Number(clean.amount) : undefined)}
        fields={[
          { key: "trx_id", label: "Invoice", type: "select", required: true, options: (open.data || []).map((t) => ({ label: `${t.trx_number} · due ${fmtMoney(t.amount_due_remaining)}`, value: t.id })) },
          { key: "amount", label: "Amount (blank: as much as possible)", type: "number", step: "any", min: 0 },
        ]}
      />
      <FormDrawer show={form === "reverse"} title="Reverse receipt" initial={{}} onHide={() => setForm(null)} onDone={refresh} onSave={(clean) => finService.reverseReceipt(id, clean.reason)} fields={[{ key: "reason", label: "Why", type: "textarea", required: true, max: 200 }]} />
    </SlidePanel>
  );
}

function Receipts() {
  const roles = useRoles();
  const [f, setF] = useState({ q: "", unapplied: false });
  const { data, loading, error, reload } = useLoad(() => finService.listReceipts({ q: f.q || undefined, unapplied: f.unapplied ? 1 : undefined }), [f.q, f.unapplied]);
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        {roles.cashier ? <PrimaryButton onClick={() => setAdding(true)}>Receive payment</PrimaryButton> : null}
        <input className="form-control" style={{ ...inputStyle, width: 260 }} placeholder="Number, customer, reference" aria-label="Search receipts" onKeyDown={(e) => e.key === "Enter" && setF({ ...f, q: e.target.value.trim() })} />
        <label style={{ fontSize: 14 }}>
          <input type="checkbox" checked={f.unapplied} onChange={(e) => setF({ ...f, unapplied: e.target.checked })} /> With unapplied cash
        </label>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No receipts" />}
          columns={[
            { label: "Number", render: (r) => <b>{r.receipt_number}</b> },
            { label: "Date", render: (r) => formatDate(r.receipt_date) },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Method", render: (r) => r.method_type.toLowerCase().replace(/_/g, " ") },
            { label: "Amount", num: true, render: (r) => `${fmtMoney(r.amount)} ${r.currency}` },
            { label: "Unapplied", num: true, render: (r) => (Number(r.unapplied_amount) ? fmtMoney(r.unapplied_amount) : "—") },
            { label: "For", render: (r) => r.source_doc_number || (r.purpose === "ADVANCE" ? "Advance" : "") },
            { label: "Status", render: (r) => <Badge status={r.status === "CONFIRMED" ? "POSTED" : r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <ReceiptView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      <ReceiptForm show={adding} onHide={() => setAdding(false)} onDone={reload} />
    </>
  );
}

/* ------------------------------ cheques ------------------------------ */

function Cheques() {
  const roles = useRoles();
  const opts = useFinOptions(["bank-accounts"]);
  const [status, setStatus] = useState("IN_HAND");
  const { data, loading, error, reload } = useLoad(() => finService.listCheques({ status: status || undefined }), [status]);
  const remits = useLoad(() => finService.listRemittances(), []);
  const [chosen, setChosen] = useState([]);
  const [form, setForm] = useState(null);
  const rows = data || [];
  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const after = () => {
    setChosen([]);
    reload();
    remits.reload();
  };
  const selectedTotal = rows.filter((r) => chosen.includes(r.id)).reduce((s, r) => s + Number(r.amount), 0);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div style={{ width: 180 }}>
          <AppDropdown
            id="pdc-st"
            value={status}
            options={[
              { label: "In hand", value: "IN_HAND" },
              { label: "At the bank", value: "DEPOSITED" },
              { label: "Cleared", value: "CLEARED" },
              { label: "Bounced", value: "BOUNCED" },
              { label: "All", value: "" },
            ]}
            onChange={(e) => {
              setStatus(e.value);
              setChosen([]);
            }}
          />
        </div>
        {roles.treasury && status === "IN_HAND" ? (
          <PrimaryButton disabled={!chosen.length} onClick={() => setForm("deposit")}>
            Deposit {chosen.length ? `${chosen.length} (${fmtMoney(selectedTotal)})` : ""}
          </PrimaryButton>
        ) : null}
        {roles.treasury && status === "DEPOSITED" ? (
          <PrimaryButton disabled={!chosen.length} onClick={async () => (await act(() => finService.clearCheques({ pdc_ids: chosen, clear_date: todayYmd() }))) && after()}>
            Mark cleared
          </PrimaryButton>
        ) : null}
        <span style={hint}>A cheque can be deposited on or after its date.</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={rows}
          minWidth={980}
          empty={<EmptyState title="No cheques" />}
          columns={[
            ...(["IN_HAND", "DEPOSITED"].includes(status) && roles.treasury ? [{ label: "", width: 30, render: (r) => <input type="checkbox" aria-label={`Select ${r.pdc_number}`} checked={chosen.includes(r.id)} disabled={status === "IN_HAND" && String(r.cheque_date).slice(0, 10) > todayYmd()} onChange={() => toggle(r.id)} /> }] : []),
            { label: "PDC", render: (r) => <b>{r.pdc_number}</b> },
            { label: "From", render: (r) => r.party_name },
            { label: "Cheque", render: (r) => `${r.cheque_number} · ${r.drawee_bank}` },
            { label: "Dated", render: (r) => formatDate(r.cheque_date) },
            { label: "Amount", num: true, render: (r) => `${fmtMoney(r.amount)} ${r.currency}` },
            { label: "Receipt", render: (r) => r.receipt_number },
            { label: "Status", render: (r) => <Badge status={r.status === "IN_HAND" ? "OPEN" : r.status === "DEPOSITED" ? "SUBMITTED" : r.status === "CLEARED" ? "COMPLETED" : r.status === "BOUNCED" ? "FAILED" : r.status} /> },
            {
              label: "",
              render: (r) => (
                <span className="d-inline-flex gap-2">
                  {r.status === "DEPOSITED" && roles.treasury ? <button type="button" style={linkBtn} onClick={() => setForm({ kind: "bounce", row: r })}>Bounced</button> : null}
                  {r.status === "IN_HAND" && roles.cashier ? <button type="button" style={linkBtn} onClick={() => setForm({ kind: "return", row: r })}>Give back</button> : null}
                  {["IN_HAND", "BOUNCED"].includes(r.status) && roles.cashier ? <button type="button" style={linkBtn} onClick={() => setForm({ kind: "replace", row: r })}>Replace</button> : null}
                </span>
              ),
            },
          ]}
        />
      </LoadState>
      <h6 className="mt-4">Deposits</h6>
      <DataTable
        rows={remits.data || []}
        minWidth={600}
        empty={<div style={hint}>None yet.</div>}
        columns={[
          { label: "Deposit", render: (d) => d.remittance_number },
          { label: "Date", render: (d) => formatDate(d.deposit_date) },
          { label: "Bank", render: (d) => d.bank_account_name },
          { label: "Cheques", num: true, render: (d) => d.cheques },
          { label: "Total", num: true, render: (d) => `${fmtMoney(d.total)} ${d.currency}` },
          { label: "Status", render: (d) => <Badge status={d.status === "SETTLED" ? "COMPLETED" : "SUBMITTED"} /> },
        ]}
      />
      <FormDrawer
        show={form === "deposit"}
        title="Deposit cheques"
        initial={{ deposit_date: todayYmd() }}
        onHide={() => setForm(null)}
        onDone={after}
        onSave={(clean) => finService.depositCheques({ ...clean, pdc_ids: chosen })}
        fields={[
          { key: "bank_account_id", label: "Bank account", type: "select", required: true, options: opts["bank-accounts"] || [] },
          { key: "deposit_date", label: "Deposit date", type: "date", required: true },
        ]}
      />
      <FormDrawer
        show={!!form && ["bounce", "return"].includes(form.kind)}
        title={form && form.kind === "bounce" ? `Bounced: ${form.row.pdc_number}` : form && form.row ? `Give back ${form.row.pdc_number}` : ""}
        initial={{}}
        onHide={() => setForm(null)}
        onDone={after}
        onSave={(clean) => finService.chequeAction(form.row.id, form.kind, clean)}
        fields={[{ key: "reason", label: form && form.kind === "bounce" ? "Bank's reason" : "Why", type: "textarea", required: true, max: 200 }]}
      >
        <div style={hint}>The receipt is reversed and the invoices it paid are open again.</div>
      </FormDrawer>
      {form && form.kind === "replace" ? (
        <ReceiptForm
          show
          title={`Replace ${form.row.pdc_number}`}
          hideCustomer
          defaultAmount={Number(form.row.amount)}
          onHide={() => setForm(null)}
          onDone={after}
          save={(body) => finService.chequeAction(form.row.id, "replace", { method_id: body.method_id, amount: body.amount, cheque: body.cheque, reference: body.reference, bank_account_id: body.bank_account_id })}
        />
      ) : null}
    </>
  );
}

/* ------------------------------ cash drawers ------------------------------ */

function Drawers() {
  const roles = useRoles();
  const opts = useFinOptions(["cash-drawers", "bank-accounts"]);
  const { data, loading, error, reload } = useLoad(() => finService.listSessions({}), []);
  const [form, setForm] = useState(null);
  return (
    <>
      <div className="d-flex gap-2 align-items-center mb-3">
        {roles.cashier ? <PrimaryButton onClick={() => setForm({ kind: "open" })}>Open my drawer</PrimaryButton> : null}
        <span style={hint}>Cash receipts go into the cashier's open drawer; count it at the end of the shift.</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          minWidth={980}
          empty={<EmptyState title="No drawer sessions" />}
          columns={[
            { label: "Drawer", render: (s) => s.drawer_name },
            { label: "Cashier", render: (s) => s.cashier_name || "Owner / admin" },
            { label: "Opened", render: (s) => formatDateTime(s.opened_at) },
            { label: "Float", num: true, render: (s) => fmtMoney(s.opening_float) },
            { label: "Cash in", num: true, render: (s) => fmtMoney(s.cash_received) },
            { label: "Counted", num: true, render: (s) => (s.counted_amount === null ? "—" : fmtMoney(s.counted_amount)) },
            { label: "Difference", num: true, render: (s) => (s.variance === null ? "—" : <span style={{ color: Number(s.variance) ? "#991B1B" : undefined }}>{fmtMoney(s.variance)}</span>) },
            { label: "Status", render: (s) => <Badge status={s.status === "OPEN" ? "OPEN" : "CLOSED"} /> },
            { label: "", render: (s) => (s.status === "OPEN" && roles.cashier ? <button type="button" style={linkBtn} onClick={() => setForm({ kind: "close", row: s })}>Count and close</button> : null) },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={!!form && form.kind === "open"}
        title="Open drawer"
        initial={{ opening_float: 0 }}
        onHide={() => setForm(null)}
        onDone={reload}
        onSave={(clean) => finService.openSession({ drawer_id: clean.drawer_id, opening_float: Number(clean.opening_float || 0) })}
        fields={[
          { key: "drawer_id", label: "Drawer", type: "select", required: true, options: opts["cash-drawers"] || [] },
          { key: "opening_float", label: "Opening float", type: "number", step: "any", min: 0 },
        ]}
      />
      <FormDrawer
        show={!!form && form.kind === "close"}
        title="Count and close"
        initial={{}}
        onHide={() => setForm(null)}
        onDone={reload}
        onSave={(clean) => finService.closeSession(form.row.id, { counted_amount: Number(clean.counted_amount), banked_amount: Number(clean.banked_amount || 0), bank_account_id: clean.bank_account_id, remarks: clean.remarks })}
        fields={[
          { key: "counted_amount", label: "Cash counted (including the float)", type: "number", step: "any", min: 0, required: true },
          { key: "banked_amount", label: "Taken to the bank", type: "number", step: "any", min: 0 },
          { key: "bank_account_id", label: "Bank account", type: "select", options: opts["bank-accounts"] || [] },
          { key: "remarks", label: "Remarks", type: "textarea", max: 500 },
        ]}
      >
        {form && form.row ? <div style={hint}>Expected: {fmtMoney(Number(form.row.opening_float) + Number(form.row.cash_received))}</div> : null}
      </FormDrawer>
    </>
  );
}

/* ------------------------------ petty cash ------------------------------ */

function PettyCash() {
  const roles = useRoles();
  const gl = useGlContext();
  const opts = useFinOptions(["petty-cash-funds", "bank-accounts", "tax-rates"]);
  const { data, loading, error, reload } = useLoad(() => finService.listPettyVouchers({}), []);
  const [form, setForm] = useState(null);
  const [account, setAccount] = useState(null);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <PrimaryButton onClick={() => setForm("EXPENSE")}>New voucher</PrimaryButton>
        {roles.treasury ? <SecondaryButton onClick={() => setForm("TOPUP")}>Top up a fund</SecondaryButton> : null}
        <span style={hint}>
          {(opts["petty-cash-funds"] || []).map((f) => `${f.row.name}: ${fmtMoney(f.row.balance)} of ${fmtMoney(f.row.float_amount)}`).join(" · ")}
        </span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          minWidth={900}
          empty={<EmptyState title="No vouchers" />}
          columns={[
            { label: "Voucher", render: (v) => <b>{v.voucher_number}</b> },
            { label: "Date", render: (v) => formatDate(v.voucher_date) },
            { label: "Fund", render: (v) => v.fund_name },
            { label: "Type", render: (v) => (v.voucher_type === "TOPUP" ? "Top-up" : "Expense") },
            { label: "Description", render: (v) => v.description },
            { label: "Account", render: (v) => (v.expense_account ? <span style={{ fontFamily: "monospace", fontSize: 12 }}>{v.expense_account}</span> : "—") },
            { label: "Total", num: true, render: (v) => `${fmtMoney(v.total)} ${v.currency}` },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={!!form}
        title={form === "TOPUP" ? "Top up petty cash" : "Petty cash voucher"}
        initial={{ voucher_date: todayYmd(), tax_rate_code: "AE_STD" }}
        onHide={() => {
          setForm(null);
          setAccount(null);
        }}
        onDone={() => {
          reload();
          setAccount(null);
        }}
        width={620}
        onSave={(clean) => finService.createPettyVoucher({ ...clean, voucher_type: form, amount: Number(clean.amount), account: form === "EXPENSE" ? account : undefined })}
        fields={[
          { key: "fund_id", label: "Fund", type: "select", required: true, options: opts["petty-cash-funds"] || [] },
          { key: "voucher_date", label: "Date", type: "date", required: true },
          { key: "amount", label: form === "TOPUP" ? "Amount" : "Amount before VAT", type: "number", step: "any", min: 0, required: true },
          ...(form === "TOPUP"
            ? [{ key: "bank_account_id", label: "From bank account", type: "select", required: true, options: opts["bank-accounts"] || [] }]
            : [
                { key: "tax_rate_code", label: "VAT", type: "select", options: (opts["tax-rates"] || []).map((o) => ({ label: o.label, value: o.code })) },
                { key: "payee", label: "Paid to", max: 150 },
                { key: "description", label: "What for", required: true, max: 500 },
              ]),
        ]}
      >
        {form === "EXPENSE" && gl.structure ? (
          <Field label="Expense account" required>
            <AccountPicker id="pc-acct" structure={gl.structure} companyValue={gl.ledger ? gl.ledger.company_value : null} value={account} onChange={setAccount} />
          </Field>
        ) : null}
      </FormDrawer>
    </>
  );
}

/* ------------------------------ write-offs and aging ------------------------------ */

function WriteOffs() {
  const roles = useRoles();
  const { data, loading, error, reload } = useLoad(() => finService.listAdjustments(), []);
  const open = useLoad(() => finService.listTransactions({ open: 1, trx_class: "INV", size: 200 }), []);
  const [adding, setAdding] = useState(false);
  return (
    <>
      {roles.treasury ? (
        <div className="mb-3">
          <PrimaryButton onClick={() => setAdding(true)}>Write off</PrimaryButton>
        </div>
      ) : null}
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No write-offs" />}
          columns={[
            { label: "Number", render: (a) => a.adj_number },
            { label: "Invoice", render: (a) => a.trx_number },
            { label: "Customer", render: (a) => a.customer_name },
            { label: "Amount", num: true, render: (a) => `${fmtMoney(a.amount)} ${a.currency}` },
            { label: "Reason", render: (a) => a.reason },
            { label: "Status", render: (a) => <Badge status={a.status} /> },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={adding}
        title="Write off a receivable"
        initial={{ adj_type: "WRITE_OFF" }}
        onHide={() => setAdding(false)}
        onDone={() => {
          reload();
          open.reload();
        }}
        onSave={(clean) => finService.requestWriteOff({ ...clean, amount: Number(clean.amount) })}
        fields={[
          { key: "trx_id", label: "Invoice", type: "select", required: true, options: (open.data || []).map((t) => ({ label: `${t.trx_number} · ${t.customer_name} · due ${fmtMoney(t.amount_due_remaining)}`, value: t.id })) },
          { key: "adj_type", label: "Type", type: "select", required: true, options: [{ label: "Bad debt", value: "WRITE_OFF" }, { label: "Small balance", value: "SMALL_BALANCE" }] },
          { key: "amount", label: "Amount", type: "number", step: "any", min: 0, required: true },
          { key: "reason", label: "Reason", type: "textarea", required: true, max: 500 },
        ]}
      >
        <div style={hint}>The Finance controller approves write-offs (Approvals).</div>
      </FormDrawer>
    </>
  );
}

function Aging() {
  const [asOf, setAsOf] = useState(todayYmd());
  const { data, loading, error, reload } = useLoad(() => finService.getAging({ as_of: asOf }), [asOf]);
  const [stmt, setStmt] = useState(null);
  const labels = { CURRENT: "Not due", "1-30": "1-30", "31-60": "31-60", "61-90": "61-90", "91-120": "91-120", "120+": "120+" };
  return (
    <>
      <div className="d-flex gap-2 align-items-center mb-3">
        <input type="date" className="form-control" style={{ ...inputStyle, width: 170 }} aria-label="As of" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        <span style={hint}>Open invoice instalments by days past their due date.</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data ? (
          <DataTable
            rows={data.customers}
            rowKey={(c) => c.account_number}
            minWidth={900}
            empty={<EmptyState title="Nothing outstanding" />}
            columns={[
              { label: "Customer", render: (c) => <button type="button" style={linkBtn} onClick={() => setStmt(c)}>{c.customer_name}</button> },
              ...data.buckets.map((b) => ({ label: labels[b], num: true, render: (c) => (c[b] ? fmtMoney(c[b]) : "—") })),
              { label: "Total", num: true, render: (c) => <b>{fmtMoney(c.total)}</b> },
            ]}
          />
        ) : null}
      </LoadState>
      {stmt ? <Statement customer={stmt} onHide={() => setStmt(null)} /> : null}
    </>
  );
}

function Statement({ customer, onHide }) {
  const first = `${todayYmd().slice(0, 8)}01`;
  const [range, setRange] = useState({ date_from: first, date_to: todayYmd() });
  const { data, loading, error, reload } = useLoad(() => finService.getStatement(customer.customer_account_id, range), [range.date_from, range.date_to]);
  return (
    <SlidePanel show onHide={onHide} title={`Statement – ${customer.customer_name}`} width={900}>
      <div className="d-flex gap-2 align-items-end mb-3">
        <Field label="From">
          <input type="date" className="form-control" style={inputStyle} value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} />
        </Field>
        <Field label="To">
          <input type="date" className="form-control" style={inputStyle} value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} />
        </Field>
        <PrimaryButton style={{ marginBottom: 16 }} onClick={() => finService.statementPdf(customer.customer_account_id, range, `statement-${customer.account_number}.pdf`)}>
          PDF
        </PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data ? (
          <>
            <div style={hint}>Opening {fmtMoney(data.opening)} · closing {fmtMoney(data.closing)} {data.currency}</div>
            <DataTable
              rows={data.lines}
              rowKey={(l, i) => i}
              minWidth={760}
              columns={[
                { label: "Date", render: (l) => formatDate(l.date) },
                { label: "Reference", render: (l) => l.reference },
                { label: "Type", render: (l) => l.kind },
                { label: "Debit", num: true, render: (l) => (l.debit ? fmtMoney(l.debit) : "") },
                { label: "Credit", num: true, render: (l) => (l.credit ? fmtMoney(l.credit) : "") },
                { label: "Balance", num: true, render: (l) => fmtMoney(l.balance) },
              ]}
            />
          </>
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}

export default function CashBank() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "receipts";
  return (
    <div style={pageStyle}>
      <PageHeader title="Cash & Bank" subtitle="Receipts, cheques, cash drawers, petty cash and receivables aging" />
      <Tabs
        active={tab}
        onChange={(t) => setParams({ tab: t })}
        tabs={[
          { key: "receipts", label: "Receipts" },
          { key: "cheques", label: "Cheques (PDC)" },
          { key: "drawers", label: "Cash drawers" },
          { key: "petty", label: "Petty cash" },
          { key: "writeoffs", label: "Write-offs" },
          { key: "aging", label: "Aging" },
        ]}
      />
      {tab === "receipts" ? <Receipts /> : null}
      {tab === "cheques" ? <Cheques /> : null}
      {tab === "drawers" ? <Drawers /> : null}
      {tab === "petty" ? <PettyCash /> : null}
      {tab === "writeoffs" ? <WriteOffs /> : null}
      {tab === "aging" ? <Aging /> : null}
    </div>
  );
}
